/**
 * RoleEditorDrawer — replace a user's role set (P1 Tier-A, users.role_assign).
 * Reads the user's current roles (from the list row), lists every assignable
 * role from GET /admin/roles, previews each role's permissions (descriptions
 * from GET /admin/permissions), and PUTs the chosen set to
 * PUT /admin/users/{id}/roles (replace-set semantics; Idempotency-Key auto).
 * Server RBAC is the real enforcement; the submit affordance is gated UX on
 * `users.role_assign`.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, GPill, Skeleton } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { useRequirePermission } from "@/features/admin/hooks/useRequirePermission";
import {
  useAssignUserRoles,
  usePermissionCatalogue,
  useRoles,
} from "@/features/admin/users/hooks";
import type { AdminUser } from "@/features/admin/users/api";

export interface RoleEditorDrawerProps {
  user: AdminUser | null;
  onClose: () => void;
}

export function RoleEditorDrawer({ user, onClose }: RoleEditorDrawerProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const canAssign = useRequirePermission("users.role_assign");

  const { data: rolesData, isLoading: rolesLoading } = useRoles();
  const { data: permCatalogue } = usePermissionCatalogue();
  const assign = useAssignUserRoles();

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Seed the selection from the user's current roles whenever a user opens.
  useEffect(() => {
    setSelected(new Set(user?.roles ?? []));
  }, [user]);

  const permDescription = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of permCatalogue?.groups ?? [])
      for (const p of g.permissions) map.set(p.slug, p.description);
    return map;
  }, [permCatalogue]);

  const isOpen = user !== null;
  const toggle = (slug: string): void =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const handleSubmit = (): void => {
    if (!user) return;
    assign.mutate(
      { userId: user.id, roleSlugs: Array.from(selected) },
      {
        onSuccess: () => {
          toast.success(t("users.roles.savedToast"));
          onClose();
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  return (
    <BottomSheet open={isOpen} onClose={onClose} title={t("users.roles.title")} desktop={isDesktop}>
      {!user ? null : rolesLoading ? (
        <div className="flex flex-col gap-3 py-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div data-testid="role-editor" className="flex flex-col gap-4 overflow-y-auto">
          <div>
            <div className="font-medium">{user.display_name}</div>
            <div className="text-sm text-[var(--g-secondary)]">{user.email}</div>
          </div>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
              {t("users.roles.assignable")}
            </h3>
            <ul className="flex flex-col gap-2">
              {(rolesData?.items ?? []).map((role) => {
                const checked = selected.has(role.slug);
                return (
                  <li
                    key={role.slug}
                    className="rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3"
                  >
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        data-testid={`role-toggle-${role.slug}`}
                        checked={checked}
                        onChange={() => toggle(role.slug)}
                        aria-label={role.name}
                        className="mt-1 size-5 shrink-0 accent-[var(--g-primary)]"
                      />
                      <span className="flex flex-1 flex-col gap-1.5">
                        <span className="font-medium">{role.name}</span>
                        <span className="flex flex-wrap gap-1" aria-label={t("users.roles.permPreview")}>
                          {role.permissions.map((slug) => (
                            <GPill key={slug} tone="neutral" size="sm">
                              {permDescription.get(slug) ?? slug}
                            </GPill>
                          ))}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={assign.isPending} className="min-h-[44px]">
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              data-testid="role-editor-submit"
              onClick={handleSubmit}
              disabled={!canAssign}
              loading={assign.isPending}
              className="min-h-[44px]"
            >
              {t("users.roles.submit")}
            </Button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

export default RoleEditorDrawer;
